"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Search, ChevronLeft, Clock, X, Loader2, AlertCircle, Mic, Video, User } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { usePodcastSearchHistory } from "@/stores/podcast-search-store";
import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import Link from "next/link";
import { usePlayer } from "@/contexts/player-context";
import { useUser } from "@/lib/context/user-context";
import { useIsMobile } from "@/hooks/use-mobile";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PodcastResult {
  id: string;
  title: string;
  author: string;
  description?: string;
  imageUrl?: string;
  categories?: string[];
}

interface ChannelResult {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  subscriberCount?: string;
  videoCount?: string;
}

interface VideoResult {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  videoId: string;
  thumbnail: string;
  duration?: number;
  durationFormatted?: string;
  viewCount?: number;
  viewCountText?: string;
  publishedTimeText?: string;
  publishDate?: string;
  description?: string;
}

type SearchMode = "podcast" | "video";

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const router = useRouter();
  const { t } = useI18n() as any;
  const { 
    setCurrentEpisode, 
    showPlayer, 
    openFullPlayer,
    setIsLoading: setPlayerLoading,
    setCleanReadData 
  } = usePlayer();
  const { session } = useUser();
  const isMobile = useIsMobile();
  
  const [query, setQuery] = React.useState("");
  const [searchMode, setSearchMode] = React.useState<SearchMode>("podcast");
  const [podcastResults, setPodcastResults] = React.useState<PodcastResult[]>([]);
  const [videoResults, setVideoResults] = React.useState<VideoResult[]>([]);
  const [channelResults, setChannelResults] = React.useState<ChannelResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");
  // 分页状态
  const [videoPage, setVideoPage] = React.useState(1);
  const [videosHasMore, setVideosHasMore] = React.useState(false);
  const [videoPaginationToken, setVideoPaginationToken] = React.useState<string | null>(null);
  
  // Ensure hook returns fallback if store is not initialized properly or mock for safety
  const store = usePodcastSearchHistory();
  const { searchHistory, clearHistory, addToHistory } = store || { searchHistory: [], clearHistory: () => {}, addToHistory: () => {} };

  // 加载 trending 视频
  const loadTrendingVideos = React.useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch("/api/youtube-trending?limit=20");
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to load trending videos");
      }
      setVideoResults(data.videos || []);
      setVideosHasMore(data.hasMore || false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trending videos");
      setVideoResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 加载 trending podcasts
  const loadTrendingPodcasts = React.useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch("/api/trending-podcasts?limit=20");
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to load trending podcasts");
      }
      setPodcastResults(data.podcasts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trending podcasts");
      setPodcastResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 🔥 Debounce 已完全移除，搜索只在用户主动触发时执行（点击搜索按钮或按 Enter）
  // 当对话框关闭时，重置状态
  React.useEffect(() => {
    if (!open) {
      setQuery("");
      setPodcastResults([]);
      setVideoResults([]);
      setChannelResults([]);
      setError("");
      setVideoPage(1);
      setVideosHasMore(false);
      setVideoPaginationToken(null);
    }
  }, [open]);
  
  // 对话框打开时，根据模式加载 trending 内容
  const hasLoadedTrendingVideo = React.useRef(false);
  const hasLoadedTrendingPodcast = React.useRef(false);
  
  React.useEffect(() => {
    if (open && searchMode === "video" && !query.trim() && videoResults.length === 0 && !hasLoadedTrendingVideo.current) {
      hasLoadedTrendingVideo.current = true;
      loadTrendingVideos();
    }
    if (open && searchMode === "podcast" && !query.trim() && podcastResults.length === 0 && !hasLoadedTrendingPodcast.current) {
      hasLoadedTrendingPodcast.current = true;
      loadTrendingPodcasts();
    }
    if (!open) {
      hasLoadedTrendingVideo.current = false;
      hasLoadedTrendingPodcast.current = false;
    }
  }, [open, searchMode, query, videoResults.length, podcastResults.length, loadTrendingVideos, loadTrendingPodcasts]);
  
  // 无限滚动：检测滚动到底部时加载更多
  const loadMoreTriggerRef = React.useRef<HTMLDivElement | null>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  
  // 使用 useCallback 稳定 handleLoadMore 函数
  const handleLoadMore = React.useCallback(() => {
    if (videosHasMore && !isLoadingMore && !isLoading && query.trim()) {
      handleSearch(undefined, query, true);
    }
  }, [videosHasMore, isLoadingMore, isLoading, query]);
  
  React.useEffect(() => {
    if (!open || searchMode !== "video" || !videosHasMore || isLoadingMore || isLoading) {
      // 如果条件不满足，断开 observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      return;
    }
    
    // 断开现有 observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // 创建新的 observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );
    
    // 观察触发元素
    const triggerElement = loadMoreTriggerRef.current;
    if (triggerElement && observerRef.current) {
      observerRef.current.observe(triggerElement);
    }
    
    // 清理
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [open, searchMode, videosHasMore, isLoadingMore, isLoading, handleLoadMore]);

  const handleSearch = async (e?: React.FormEvent, searchQuery: string = query, loadMore = false) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setVideoPage(1);
      setVideoResults([]);
      setVideosHasMore(false);
      setVideoPaginationToken(null);
    }
    setError("");
    
    try {
      const params = new URLSearchParams({
        limit: "10",
        q: searchQuery
      });
      
      // Add continuation token for video pagination
      if (loadMore && videoPaginationToken && searchMode === "video") {
        params.append("token", videoPaginationToken);
      }

      if (searchMode === "podcast") {
        const response = await fetch(`/api/search/podcasts?${params}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Search failed");
        }
        if (loadMore) {
          setPodcastResults((prev) => [...prev, ...(data.podcasts || [])]);
        } else {
          setPodcastResults(data.podcasts || []);
        }
        setVideoResults([]);
      } else {
        // Video search
        
        // Check for channel search (starts with @, no spaces)
        if (searchQuery.startsWith('@') && !searchQuery.trim().includes(' ')) {
          const channelQuery = searchQuery.substring(1);
          if (channelQuery) {
             const response = await fetch(`/api/search/channels?q=${encodeURIComponent(channelQuery)}`);
             const data = await response.json();
     
             if (!response.ok) {
               throw new Error(data.error || "Search failed");
             }
             setChannelResults(data.channels || []);
             setVideoResults([]);
             setPodcastResults([]);
             return;
          }
        }
        
        setChannelResults([]);
        
        // Check for @channel handle in query
        // Example: "@PewDiePie minecraft" -> search for "minecraft" in PewDiePie's channel
        const channelMatch = searchQuery.match(/^@([^\s]+)(?:\s+(.*))?$/);
        let endpoint = "/api/search/videos";
        let finalParams = params;
        
        if (channelMatch) {
          const channelName = channelMatch[1]; // e.g., "PewDiePie"
          const channelQuery = channelMatch[2] || ""; // e.g., "minecraft"
          
          // Map channel name to ID (mock implementation for now since we need ID)
          // Ideally we would have an endpoint to resolve handle -> ID
          // For this demo, we'll try to find it in our local map or search for the channel
          
          // We need to fetch the channel ID first or use a hardcoded map for demo
          const channelMap: Record<string, string> = {
            "pewdiepie": "UC-lHJZR3Gqxm24_Vd_AJ5Yw",
            "mrbeast": "UCX6OQ3DkcsbYNE6H8uQQuVA",
            "markiplier": "UC7_YxT-KID8kRbqZo7MyscQ",
            "dudeperfect": "UCRijo3ddMTht_IHyNSNXpNQ",
            "cocomelon": "UCbCmjCuTUZos6Inko4u57UQ",
            "tseries": "UCq-Fj5jknLsUf-MWSy4_brA",
            "5minutecrafts": "UC295-Dw_tDNtZXFeAPAW6Aw",
            "wwe": "UCJ5v_MCY6GNUBTO8-D3XoAg",
            "brightside": "UC4rlAVgAK0SGk-yTfe48Qpw",
            "linustechtips": "UCXuqSBlHAE6Xw-yeJA0Tunw",
            "mkbhd": "UCBJycsmduvYEL83R_U4JriQ",
            "veritasium": "UCHnyfMqiRRG1u-2MsSQLbXA",
            "kurzgesagt": "UCsXVk37bltHxD1rDPwtNM8Q",
            "ted": "UCAuUUnT6oDeKwE6v1NGQxug",
            "vox": "UCAuUUnT6oDeKwE6v1NGQxug"
          };
          
          const channelId = channelMap[channelName.toLowerCase()];
          
          if (channelId) {
            endpoint = "/api/youtube-channel-videos";
            finalParams = new URLSearchParams();
            finalParams.append("channelId", channelId);
            if (channelQuery) {
              finalParams.append("query", channelQuery);
            }
            finalParams.append("limit", "10");
          }
        }

        const response = await fetch(`${endpoint}?${finalParams}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Search failed");
        }
        
        if (loadMore) {
          setVideoResults((prev) => [...prev, ...(data.videos || [])]);
          setVideoPage((prev) => prev + 1);
        } else {
          setVideoResults(data.videos || []);
          setVideoPage(1);
        }
        
        // 更新分页状态
        setVideosHasMore(data.hasMore || data.pagination?.hasMore || false);
        setVideoPaginationToken(data.pagination?.token || null);
        setPodcastResults([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      if (!loadMore) {
        setPodcastResults([]);
        setVideoResults([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    addToHistory(query.trim());
    // 手动触发搜索
    handleSearch(undefined, query.trim());
  };

  const handleHistoryClick = (text: string) => {
    setQuery(text);
    // 直接触发搜索
    handleSearch(undefined, text);
  };

  const handleClearQuery = () => {
    setQuery("");
    setPodcastResults([]);
    setVideoResults([]);
    setChannelResults([]);
    const input = document.getElementById('search-input-dialog');
    if (input) input.focus();
  };

  const handlePodcastClick = (podcast: PodcastResult) => {
    addToHistory(query.trim());
    onOpenChange(false);
    router.push(`/podcast/${podcast.id}`);
  };
  
  const handleVideoClick = async (video: VideoResult) => {
    addToHistory(query.trim());
    onOpenChange(false);

    showPlayer();
    openFullPlayer();
    setPlayerLoading(true);

    const shareId = video.videoId;
    const youtubeUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
    const placeholder = {
      shareId,
      sourceType: "youtube",
      videoId: video.videoId,
      videoUrl: youtubeUrl,
      origin: "video",
      title: video.title,
      author: video.author,
      thumbnail: video.thumbnail,
      ownerAvatarUrl: video.ownerAvatarUrl,
      // ownerProfileUrl: video.ownerProfileUrl,
      hydrationPending: true,
      isHydrationPlaceholder: true,
      content: {
        shareId,
        title: video.title,
        author: video.author,
        thumbnail: video.thumbnail,
        ownerAvatarUrl: video.ownerAvatarUrl,
        videoId: video.videoId,
        videoUrl: youtubeUrl,
        origin: "video",
        hydrationPending: true,
        isHydrationPlaceholder: true,
      },
    };

    setCurrentEpisode(placeholder);
    setCleanReadData(null);

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    const token =
      session?.access_token ??
      (typeof window !== "undefined"
        ? window.localStorage.getItem("token")
        : null);
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`/api/episodes/${shareId}`, {
        method: "GET",
        headers,
      });
      if (response.ok) {
        const episodeData = await response.json();
        const shareData = (episodeData?.content ?? {}) as Record<string, any>;
        const normalizedShare = {
          ...shareData,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          content: {
            ...(shareData?.content ?? {}),
            hydrationPending: false,
            isHydrationPlaceholder: false,
          },
        };
        setCurrentEpisode(normalizedShare);
        setCleanReadData(
          normalizedShare?.content?.cleanRead ??
            shareData?.content?.cleanRead ??
            null,
        );
      } else if (response.status === 404) {
        // Fallback for YouTube videos not yet in our DB
        // Don't fetch audio URL yet - let GlobalPlayerControls handle it after quota confirmation
        
        // We don't have duration easily available here unless we fetch it from YouTube API or it was in the VideoResult
        // Assuming VideoResult might have some duration info or we rely on GlobalPlayerControls to fetch metadata if possible?
        // Actually VideoResult usually doesn't have duration unless we enriched it. 
        // If duration is missing, GlobalPlayerControls logic might wait or fail.
        // Let's try to parse it if available or pass 0.
        // video object in search results:
        // interface VideoResult { videoId, title, description, thumbnail, author, ownerAvatarUrl, publishTime }
        // It seems duration is NOT in VideoResult interface in this file (based on typical YouTube Search API response in this project).
        // However, GlobalPlayerControls will try to fetch metadata if needed.
        
        const fallbackEpisode = {
          ...placeholder,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          // Explicitly set audioUrl/videoUrl to undefined to trigger GlobalPlayer logic
          audioUrl: undefined,
          content: {
            ...(placeholder.content || {}),
            hydrationPending: false,
            isHydrationPlaceholder: false,
            audioUrl: undefined,
          },
        };
        setCurrentEpisode(fallbackEpisode);
        setCleanReadData(null);
      } else {
        throw new Error(`Failed to fetch episode data: ${response.status}`);
      }
    } catch (error) {
      console.error("Unable to open episode in global player:", error);
      // Optional: show toast error
    } finally {
      setPlayerLoading(false);
    }
  };

  const handleChannelClick = (channel: ChannelResult) => {
    onOpenChange(false);
    // Use the channel ID to navigate to the channel page
    // If we want to support custom handles later, we might need to handle that
    router.push(`/channels/${channel.id}`);
  };

  const toggleSearchMode = (mode: SearchMode) => {
    if (searchMode !== mode) {
      setSearchMode(mode);
      // 清除之前的搜索结果
      setPodcastResults([]);
      setVideoResults([]);
      setChannelResults([]);
      
      // 切换模式时，如果没有搜索词，加载对应的 trending 内容
      if (!query.trim()) {
        if (mode === "video") {
          loadTrendingVideos();
        } else if (mode === "podcast") {
          loadTrendingPodcasts();
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        showCloseButton={false} 
        className="w-screen h-screen max-w-none m-0 rounded-none border-none top-0 left-0 translate-y-0 translate-x-0 p-0 flex flex-col bg-background text-foreground gap-0 duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-[10%] data-[state=open]:slide-in-from-bottom-[10%]"
      >
        <div className="flex flex-col">
          {/* Top Bar: Back, Search Input */}
          <div className="flex items-center gap-2 pl-0 pr-3 py-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onOpenChange(false)} 
              className="h-8 w-8 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground flex-shrink-0 ml-2"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-2 min-w-0">
              <div className="relative flex-1 group">
                <div className="absolute inset-0 bg-muted/50 rounded-xl transition-colors group-focus-within:bg-muted" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search-input-dialog" 
                  type="search" 
                  value={query}
                  enterKeyHint="search"
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchMode === "podcast" 
                    ? t('search.placeholderPodcast', 'Search podcasts...') 
                    : t('search.placeholderVideo', 'Search videos...')
                  }
                  className="relative pl-9 pr-9 h-11 text-base bg-transparent border-none shadow-none focus-visible:ring-0 text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    onClick={handleClearQuery}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full bg-background/50 text-muted-foreground hover:bg-background hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* 移动端隐藏搜索按钮，使用键盘的 Search 按钮 */}
              {!isMobile && (
                <Button 
                  type="submit" 
                  className="font-medium px-4 h-11 rounded-xl flex-shrink-0 bg-foreground text-background hover:bg-foreground/90"
                >
                  {t('search.search', 'Search')}
                </Button>
              )}
            </form>
          </div>

          {/* Search Mode Toggle */}
          <div className="flex px-4 pb-2 gap-2">
             <div className="relative flex bg-muted/50 p-1 rounded-xl w-full isolate">
                {/* Animated Background */}
                <div
                  className={`absolute top-1 bottom-1 rounded-lg bg-background shadow-sm transition-all duration-300 ease-out z-0`}
                  style={{
                    left: '4px',
                    width: 'calc(50% - 4px)',
                    transform: searchMode === "video" ? 'translateX(100%)' : 'translateX(0)'
                  }}
                />
                
                <button
                  onClick={() => toggleSearchMode("podcast")}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-1.5 rounded-lg text-sm font-medium transition-colors duration-300 ${
                    searchMode === "podcast" 
                      ? "text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Mic className="w-3.5 h-3.5" />
                  {t('search.podcast', 'Podcasts')}
                </button>
                <button
                  onClick={() => toggleSearchMode("video")}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 py-1.5 rounded-lg text-sm font-medium transition-colors duration-300 ${
                    searchMode === "video" 
                      ? "text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Video className="w-3.5 h-3.5" />
                  {t('search.video', 'Videos')}
                </button>
             </div>
          </div>
          
          {/* Channel Search Hint */}
          {searchMode === "video" && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground">
                💡 {t('search.channelHint', 'Type @ to search channels, e.g. @MrBeast')}
              </p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          {/* Error State */}
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-xl flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {/* Loading State for Search Results */}
          {query && isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Search Results: Podcasts (only when user has searched) */}
          {!isLoading && query && searchMode === "podcast" && podcastResults.length > 0 && (
            <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3">
              {podcastResults.map((podcast) => (
                <div
                  key={podcast.id}
                  className="group cursor-pointer"
                  onClick={() => handlePodcastClick(podcast)}
                >
                  <div className="space-y-2">
                    <div className="relative aspect-square overflow-hidden rounded-xl bg-muted">
                      {podcast.imageUrl ? (
                        <img
                          src={podcast.imageUrl}
                          alt={podcast.title}
                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-primary/10">
                          <Search className="h-8 w-8 text-primary/30" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 px-1">
                      <h3 className="text-xs font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                        {podcast.title}
                      </h3>
                      {podcast.categories && podcast.categories[0] && (
                         <span className="inline-block text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                           {t(`search.categories.${podcast.categories[0]}`) || podcast.categories[0]}
                         </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Search Results: Channels */}
          {!isLoading && searchMode === "video" && channelResults.length > 0 && (
            <div className="space-y-2 mb-4">
              <h3 className="text-sm font-medium text-muted-foreground px-1">{t('search.channels', 'Channels')}</h3>
              <div className="grid gap-2 grid-cols-1">
                {channelResults.map((channel) => (
                  <div
                    key={channel.id}
                    onClick={() => handleChannelClick(channel)}
                    className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-xl cursor-pointer transition-colors"
                  >
                    <div className="relative h-10 w-10 rounded-full overflow-hidden bg-muted flex-shrink-0 border">
                      {channel.thumbnail && channel.thumbnail.trim() ? (
                        <img
                          src={channel.thumbnail}
                          alt={channel.title}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement("div");
                              fallback.className = "flex h-full w-full items-center justify-center bg-muted absolute inset-0";
                              fallback.innerHTML = `<svg class="h-5 w-5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-muted">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium truncate">{channel.title}</h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {channel.subscriberCount && <span>{channel.subscriberCount} subs</span>}
                        {channel.videoCount && <span>• {channel.videoCount} videos</span>}
                      </div>
                      {channel.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5 opacity-80">
                          {channel.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Results: Videos (only when user has searched) */}
          {!isLoading && query && searchMode === "video" && videoResults.length > 0 && (
            <>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {videoResults.map((video, index) => (
                  <div
                    key={`${video.videoId}-${index}`}
                    className="group cursor-pointer flex gap-3"
                    onClick={() => handleVideoClick(video)}
                  >
                    {/* Video Thumbnail (Smaller for list view on mobile) */}
                    <div className="relative w-32 aspect-video flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                       <Image
                          src={video.thumbnail || "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=320&h=180&fit=crop&crop=center"}
                          alt={video.title}
                          fill
                          className="object-cover"
                       />
                       {video.durationFormatted && (
                          <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1 rounded font-medium">
                            {video.durationFormatted}
                          </div>
                        )}
                    </div>
                    
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                      <h3 className="text-sm font-medium leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                         <span className="truncate max-w-[100px]">{video.author}</span>
                         {video.publishedTimeText && (
                           <>
                             <span>•</span>
                             <span>{video.publishedTimeText}</span>
                           </>
                         )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load More Section for Videos */}
              {videoResults.length > 0 && (
                <div className="pt-4 pb-4">
                  {/* Invisible trigger element for intersection observer */}
                  {videosHasMore && !isLoadingMore && (
                    <div
                      ref={loadMoreTriggerRef}
                      className="h-1"
                      aria-hidden="true"
                    />
                  )}

                  {/* Loading more indicator */}
                  {isLoadingMore && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  )}

                  {/* End of results message */}
                  {!videosHasMore && videoResults.length > 0 && (
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground">
                        {t('search.endOfResults', 'End of results')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Recent Searches (only show when no query) */}
          {!query && searchHistory && searchHistory.length > 0 && (
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-medium text-muted-foreground">{t('search.recent', 'Recent')}</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearHistory}
                  className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground hover:bg-transparent"
                >
                  {t('search.clear', 'Clear')}
                </Button>
              </div>
              <div className="max-h-[140px] overflow-y-auto space-y-0.5">
                {searchHistory.slice(0, 10).map((item: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => handleHistoryClick(item)}
                    className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-muted/50 transition-colors group text-left"
                  >
                    <Clock className="h-4 w-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
                    <span className="text-foreground/80 group-hover:text-foreground text-sm truncate">{item}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Loading State (show below Recent when loading trending) */}
          {!query && isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Trending Podcasts (show when no query and podcast mode) */}
          {!query && !isLoading && searchMode === "podcast" && podcastResults.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-medium text-muted-foreground">{t('search.trending', 'Trending')}</h3>
              </div>
              <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3">
                {podcastResults.map((podcast) => (
                  <div
                    key={podcast.id}
                    className="group cursor-pointer"
                    onClick={() => handlePodcastClick(podcast)}
                  >
                    <div className="space-y-2">
                      <div className="relative aspect-square overflow-hidden rounded-xl bg-muted">
                        {podcast.imageUrl ? (
                          <img
                            src={podcast.imageUrl}
                            alt={podcast.title}
                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-primary/10">
                            <Search className="h-8 w-8 text-primary/30" />
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 px-1">
                        <h3 className="text-xs font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                          {podcast.title}
                        </h3>
                        {podcast.categories && podcast.categories[0] && (
                           <span className="inline-block text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                             {t(`search.categories.${podcast.categories[0]}`) || podcast.categories[0]}
                           </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trending Videos (show when no query and video mode) */}
          {!query && !isLoading && searchMode === "video" && videoResults.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-medium text-muted-foreground">{t('search.trending', 'Trending')}</h3>
              </div>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {videoResults.map((video, index) => (
                  <div
                    key={`${video.videoId}-${index}`}
                    className="group cursor-pointer flex gap-3"
                    onClick={() => handleVideoClick(video)}
                  >
                    <div className="relative w-32 aspect-video flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                       <Image
                          src={video.thumbnail || "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=320&h=180&fit=crop&crop=center"}
                          alt={video.title}
                          fill
                          className="object-cover"
                       />
                       {video.durationFormatted && (
                          <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1 rounded font-medium">
                            {video.durationFormatted}
                          </div>
                        )}
                    </div>
                    
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                      <h3 className="text-sm font-medium leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                         <span className="truncate max-w-[100px]">{video.author}</span>
                         {video.viewCountText && (
                           <>
                             <span>•</span>
                             <span>{video.viewCountText}</span>
                           </>
                         )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results State */}
          {!isLoading && query && ((searchMode === "podcast" && podcastResults.length === 0) || (searchMode === "video" && videoResults.length === 0)) && !error && (
            <div className="text-center py-10 text-muted-foreground">
              <p>{t('search.noResults', 'No results found')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
